import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

// Reusable structural style layout object matching enterprise form configurations
const enterpriseFilterInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT MATRIX ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& .MuiSelect-select, & input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const Filters = ({ visible, meta, setMeta }) => {
    const deliveries = useSelector(state => state.deliveries);
    const profile = useSelector(state => state.profile.profile);
    const [formValues, setFormValues] = useState(meta)
    const [filterButtonDisabled, setFilterButtonDisabled] = useState(true)
    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);
    const [checkFormUse, setCheckFormUse] = useState("")
    const carriers = [
        "CANADAPOST",
        "INTELCOM",
        "RAVENFORCE",
        "TFORCE",
        "FLASHBOX",
        "UNIEXPRESS",
        "ICS"
    ]
    const onInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    const submitFilterValues = () => {
        setMeta(prevState => ({
            ...prevState,
            [`carrier_name`]: formValues["carrier_name"],
            [`shipment_status_category`]: formValues["shipment_status_category"],
            [`start_date`]: formValues["start_date"],
            [`end_date`]: formValues["end_date"],
            [`page`]: 1
        }));
        setCheckFormUse(true)
    }

    const onDateChange = (date_type, date) => {
        if(date != "" && typeof date !== "undefined" && date !=  null){
           let date_formatted = (date != null) ? moment(date).format("YYYY-MM-DD") : date
           if(date_type == "start_date"){
                let start_date = date_formatted + " 00:00:00"
                let start_date_format="";
                if(profile && profile.time_zone && profile.time_zone!==''){
                    start_date_format=moment.tz(start_date,profile.time_zone).utc().format("YYYY-MM-DD HH:mm:ss")
                }else{
                    start_date_format=moment(start_date).utc().format("YYYY-MM-DD HH:mm:ss")  
                }
                setStartDate(start_date)
                setFormValues({
                    ...formValues,
                    [`start_date`]: start_date_format
                });
           }else{
                let end_date = date_formatted + " 23:59:59"
                let end_date_format="";
                if(profile && profile.time_zone && profile.time_zone!==''){
                    end_date_format = moment.tz(end_date,profile.time_zone).utc().format("YYYY-MM-DD HH:mm:ss")
                }else{
                    end_date_format=moment(end_date).utc().format("YYYY-MM-DD HH:mm:ss")
                }
                setEndDate(end_date)
                setFormValues({
                    ...formValues,
                    [`end_date`]: end_date_format
                });
           }
        }
    }

    const resetFields = () => {
        setStartDate(null)
        setEndDate(null)
        setFormValues({
            ...formValues,
            [`carrier_name`]: [],
            [`shipment_status_category`]: [],
            [`start_date`]: "",
            [`end_date`]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            [`carrier_name`]: [],
            [`shipment_status_category`]: [],
            [`start_date`]: "",
            [`end_date`]: "",
            [`page`]: 1
        }));
    }

    const checkFormHasValues = () => {
        let disableButton = true
        if (formValues["carrier_name"].length !== 0 || formValues["shipment_status_category"].length !== 0 || formValues["start_date"] !== ""  || formValues["end_date"] !== "") {
            disableButton = false
        }

        return disableButton
    }

    useEffect(() => {
        setCheckFormUse("")
        setFilterButtonDisabled(checkFormHasValues())
    }, [formValues])

    useEffect(() => {
        if ((formValues["carrier_name"].length !== 0 || formValues["shipment_status_category"].length !== 0 || formValues["start_date"] !== "" || formValues["end_date"] !== "") && checkFormUse === '') {
            setFormValues({
                ...formValues,
                [`carrier_name`]: [],
                [`shipment_status_category`]: [],
                [`start_date`]: "",
                [`end_date`]: ""
            });
            setMeta(prevState => ({
                ...prevState,
                [`carrier_name`]: [],
                [`shipment_status_category`]: [],
                [`start_date`]: "",
                [`end_date`]: "",
                [`page`]: 1
            }));
            setStartDate(null)
            setEndDate(null)
        }
    }, [deliveries])

    return (
        <>
            {visible &&
                <Box 
                    className="filtercontainer"
                    sx={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        p: 3,
                        mb: 3,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box className="filteraccordion" id="sc-filteraccordion">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Grid container spacing={2.5}>
                                
                                {/* 1. Carrier Specification Multiple Selection Field Dropdown */}
                                {(profile.hide_carrier === false) && 
                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth variant="outlined" sx={enterpriseFilterInputStyles}>
                                            <InputLabel id="carrier-name-select-label">Carrier Name</InputLabel>
                                            <Select
                                                multiple
                                                value={formValues && formValues.carrier_name}
                                                name={"carrier_name"}
                                                labelId="carrier-name-select-label"
                                                id="demo-simple-select"
                                                label="Carrier Name"
                                                onChange={onInputChange}
                                            >
                                                {carriers && carriers.map((item) => {
                                                    return <MenuItem key={item} value={item}>{item}</MenuItem>
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                }

                                {/* 2. Shipment Pipeline Status Metric Selection Dropdown */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth variant="outlined" sx={enterpriseFilterInputStyles}>
                                        <InputLabel id="order-status-select-label">Shipment Status</InputLabel>
                                        <Select
                                            value={formValues && formValues.shipment_status_category}
                                            name={"shipment_status_category"} 
                                            labelId="order-status-select-label"
                                            id="demo-simple-select"
                                            label="Shipment Status"
                                            onChange={onInputChange}
                                        >
                                            <MenuItem value={"Label Generated"}>Label Generated</MenuItem>
                                            <MenuItem value={"Manifest Closed"}>Manifest Closed</MenuItem>
                                            <MenuItem value={"Picked Up"}>Picked Up</MenuItem>
                                            <MenuItem value={"In Transit"}>In Transit</MenuItem>
                                            <MenuItem value={"Issues"}>Issues</MenuItem>
                                            <MenuItem value={"Delivered"}>Delivered</MenuItem>
                                            <MenuItem value={"Untracked"}>Untracked</MenuItem>
                                            <MenuItem value={"Voided"}>Voided</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* 3. Chronological Operational Framework Date Span Pickers */}
                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <DatePicker
                                                label="Start Date"
                                                value={startDate}
                                                disableFuture={true}
                                                inputFormat={"yyyy-MM-dd"}
                                                onChange={(newValue) => {
                                                    onDateChange("start_date", newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        sx={enterpriseFilterInputStyles}
                                                        {...params}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <DatePicker
                                                label="End Date"
                                                value={endDate}
                                                disableFuture={true}
                                                inputFormat={"yyyy-MM-dd"}
                                                onChange={(newValue) => {
                                                    onDateChange("end_date", newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        sx={enterpriseFilterInputStyles}
                                                        {...params}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* 4. Functional Execution Footer Actions Anchor Layout Layer */}
                                <Grid item xs={12}>
                                    <Box 
                                        className="filterfooter"
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: 1.5,
                                            pt: 1,
                                        }}
                                    >
                                        <Button 
                                            variant="outlined" 
                                            onClick={resetFields} 
                                            disabled={filterButtonDisabled}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                fontSize: '13.5px',
                                                px: 3,
                                                height: '38px',
                                                borderColor: '#E2E8F0',
                                                color: '#475569',
                                                '&:hover': {
                                                    borderColor: '#CBD5E1',
                                                    backgroundColor: '#F1F5F9',
                                                    color: '#1E293B'
                                                },
                                                '&.Mui-disabled': {
                                                    borderColor: '#E2E8F0',
                                                    color: '#CBD5E1'
                                                }
                                            }}
                                        >
                                            Clear
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            onClick={submitFilterValues} 
                                            disabled={filterButtonDisabled}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                fontSize: '13.5px',
                                                px: 4,
                                                height: '38px',
                                                boxShadow: 'none',
                                                backgroundColor: '#1976d2',
                                                color: '#FFFFFF',
                                                '&:hover': { 
                                                    backgroundColor: '#115293',
                                                    boxShadow: 'none' 
                                                },
                                                '&.Mui-disabled': {
                                                    backgroundColor: '#E2E8F0',
                                                    color: '#94A3B8'
                                                }
                                            }}
                                        >
                                            Filter
                                        </Button>
                                    </Box>
                                </Grid>

                            </Grid>
                        </LocalizationProvider>
                    </Box>
                </Box>
            }
        </>
    )
}

export default Filters;